import { useEffect } from "react";
import { useRoute, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { useCreateBlockNote } from "@blocknote/react";
import { BlockNoteView } from "@blocknote/mantine";
import "@blocknote/mantine/style.css";
import type { Block } from "@blocknote/core";
import { ArrowLeft, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  featuredImage: string | null;
  category: string | null;
  status: "draft" | "published";
  content: Block[];
  publishedAt: string | null;
  createdAt: string;
}

async function fetchPostBySlug(slug: string): Promise<BlogPost> {
  const res = await fetch(`/api/admin/blog-posts/by-slug/${slug}`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error("Post not found");
  return res.json();
}

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

export default function BlogPostPreview() {
  const { user } = useAuth();
  const [, navigate] = useLocation();
  const [, params] = useRoute("/blog/:slug");
  const slug = params?.slug ?? "";

  const canPreview = user?.role === "admin" || user?.role === "platform_assistant";

  const { data: post, isLoading, isError } = useQuery({
    queryKey: ["/api/admin/blog-posts/by-slug", slug],
    queryFn: () => fetchPostBySlug(slug),
    enabled: canPreview && !!slug,
  });

  const editor = useCreateBlockNote({ editable: false });

  useEffect(() => {
    if (post && Array.isArray(post.content) && post.content.length > 0) {
      editor.replaceBlocks(editor.document, post.content);
    }
  }, [editor, post]);

  if (!canPreview) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="max-w-md overflow-hidden rounded-3xl bg-card shadow-sm outline outline-1 outline-offset-[-1px] outline-border">
          <div className="px-6 py-5">
            <h3 className="text-xl font-bold text-foreground">Access Denied</h3>
            <p className="mt-1 text-sm text-slate-500">You do not have permission to view this page.</p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
      </div>
    );
  }

  if (isError || !post) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="max-w-md overflow-hidden rounded-3xl bg-card shadow-sm outline outline-1 outline-offset-[-1px] outline-border">
          <div className="px-6 py-5">
            <h3 className="text-xl font-bold text-foreground">Post Not Found</h3>
            <p className="mt-1 text-sm text-slate-500">This post does not exist or could not be loaded.</p>
            <Button className="mt-4 rounded-2xl" variant="outline" onClick={() => navigate("/blog")}>
              Back to Blog
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-8">
      {/* Header bar */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" className="rounded-xl" onClick={() => navigate("/blog")}>
          <ArrowLeft className="mr-1.5 h-4 w-4" />
          Back to Blog
        </Button>
        {post.status === "draft" && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-xs font-bold text-amber-600">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
            Draft — not visible to the public
          </span>
        )}
      </div>

      {/* Article */}
      <article className="space-y-6">
        {/* Featured image */}
        {post.featuredImage && (
          <div className="overflow-hidden rounded-3xl">
            <img
              src={post.featuredImage}
              alt={post.title}
              className="h-72 w-full object-cover"
            />
          </div>
        )}

        {/* Meta */}
        <div className="space-y-3">
          {post.category && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
              <Tag className="h-3 w-3" />
              {post.category}
            </span>
          )}
          <h1 className="text-4xl font-extrabold tracking-tight text-slate-900">{post.title}</h1>
          {post.excerpt && (
            <p className="text-lg text-slate-500">{post.excerpt}</p>
          )}
          <p className="text-sm text-slate-400">
            {post.publishedAt ? formatDate(post.publishedAt) : formatDate(post.createdAt)}
          </p>
        </div>

        {/* Divider */}
        <hr className="border-slate-200" />

        {/* Content */}
        <div className="prose-slate max-w-none">
          <BlockNoteView editor={editor} theme="light" />
        </div>
      </article>
    </div>
  );
}
